import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { login as apiLogin, customerLogin as apiCustomerLogin, getMe } from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    try {
      const savedUser = localStorage.getItem('agrosvet_user');
      return savedUser ? JSON.parse(savedUser) : null;
    } catch {
      return null;
    }
  });
  const [activeClinicId, setActiveClinicId] = useState(() => {
    return localStorage.getItem('agrosvet_active_clinic') || null;
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  // Sync activeClinicId when user changes
  useEffect(() => {
    if (user) {
      const clinicIds = user.clinic_ids || [];
      if (clinicIds.length > 0) {
        const stored = localStorage.getItem('agrosvet_active_clinic');
        if (stored && clinicIds.includes(stored)) {
          setActiveClinicId(stored);
        } else {
          const first = clinicIds[0];
          setActiveClinicId(first);
          localStorage.setItem('agrosvet_active_clinic', first);
        }
      } else {
        setActiveClinicId(null);
        localStorage.removeItem('agrosvet_active_clinic');
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const checkAuth = async () => {
    const token = localStorage.getItem('agrosvet_token');
    const savedUser = localStorage.getItem('agrosvet_user');
    
    if (token && savedUser) {
      try {
        const parsedUser = JSON.parse(savedUser);
        setUser(parsedUser);
        
        if (parsedUser.role !== 'customer') {
          const response = await getMe();
          setUser(response.data);
          localStorage.setItem('agrosvet_user', JSON.stringify(response.data));
        }
      } catch (error) {
        // Only clear session on auth errors (401), not network errors
        if (error.response?.status === 401) {
          localStorage.removeItem('agrosvet_token');
          localStorage.removeItem('agrosvet_user');
          localStorage.removeItem('agrosvet_active_clinic');
          setUser(null);
        }
        // On network errors, keep the cached user so the app doesn't redirect
      }
    } else {
      setUser(null);
    }
    setLoading(false);
  };

  const login = async (phone, password) => {
    try {
      const response = await apiLogin(phone, password);
      const { access_token, user: userData } = response.data;
      localStorage.setItem('agrosvet_token', access_token);
      localStorage.setItem('agrosvet_user', JSON.stringify(userData));
      
      // Set active clinic
      const clinicIds = userData.clinic_ids || [];
      if (clinicIds.length > 0) {
        localStorage.setItem('agrosvet_active_clinic', clinicIds[0]);
        setActiveClinicId(clinicIds[0]);
      }
      
      setUser(userData);
      return { success: true };
    } catch (error) {
      const isNetwork = !error.response && (error.code === 'ECONNABORTED' || error.message?.includes('Network Error') || error.message?.includes('timeout'));
      const msg = isNetwork
        ? 'Sunucuya bağlanılamıyor. Lütfen birkaç saniye bekleyip tekrar deneyin.'
        : (error.response?.data?.detail || 'Telefon numarası veya şifre hatalı');
      return { success: false, error: msg };
    }
  };

  const customerLoginFn = async (phone, password) => {
    try {
      const response = await apiCustomerLogin(phone, password);
      const { access_token, user: userData } = response.data;
      localStorage.setItem('agrosvet_token', access_token);
      localStorage.setItem('agrosvet_user', JSON.stringify(userData));
      setUser(userData);
      return { success: true };
    } catch (error) {
      const isNetwork = !error.response && (error.code === 'ECONNABORTED' || error.message?.includes('Network Error') || error.message?.includes('timeout'));
      const msg = isNetwork
        ? 'Sunucuya bağlanılamıyor. Lütfen birkaç saniye bekleyip tekrar deneyin.'
        : (error.response?.data?.detail || 'Telefon numarası veya şifre hatalı');
      return { success: false, error: msg };
    }
  };

  const logout = () => {
    localStorage.removeItem('agrosvet_token');
    localStorage.removeItem('agrosvet_user');
    localStorage.removeItem('agrosvet_active_clinic');
    setUser(null);
    setActiveClinicId(null);
    window.location.href = '/';
  };

  const switchClinic = useCallback((clinicId) => {
    const clinicIds = user?.clinic_ids || [];
    if (clinicIds.includes(clinicId)) {
      setActiveClinicId(clinicId);
      localStorage.setItem('agrosvet_active_clinic', clinicId);
      // Force reload data by triggering re-render
      window.dispatchEvent(new Event('clinicSwitched'));
    }
  }, [user]);

  const getActiveClinicName = useCallback(() => {
    if (!user || !activeClinicId) return user?.clinic_name || null;
    const names = user.clinic_names || [];
    const found = names.find(c => c.id === activeClinicId);
    return found ? found.name : user.clinic_name;
  }, [user, activeClinicId]);

  const isAdmin = () => user?.role === 'systemadmin';
  const isVet = () => ['vet', 'systemadmin', 'clinic_owner', 'clinic_vet', 'clinic_tech'].includes(user?.role);
  const isCustomer = () => user?.role === 'customer';
  const isOwner = () => user?.role === 'clinic_owner' || user?.role === 'systemadmin';
  const hasMultipleClinics = () => (user?.clinic_ids || []).length > 1;

  const hasPermission = (module) => {
    if (!user) return false;
    const role = user.role;
    if (role === 'clinic_owner' || role === 'systemadmin' || role === 'vet') return true;
    const perms = user.permissions;
    if (!perms) return true;
    return !!perms[module];
  };

  const value = {
    user,
    loading,
    login,
    customerLogin: customerLoginFn,
    logout,
    isAdmin,
    isVet,
    isCustomer,
    isOwner,
    activeClinicId,
    switchClinic,
    getActiveClinicName,
    hasMultipleClinics,
    hasPermission,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
